import { ref } from 'vue';
import {
  ITEM_REFUSAL_REASON_KEYS,
  refusedItemsOf,
  type ItemRefusalReason,
  type ItemStatus,
  type RefusedItem
} from '@/models/itemRefusal';
import type { LoadingStateModel } from '@/models/loadingState';
import { prepareToast } from '@/models/toast';
import { ErrorTier } from '@/errors';
import { useAppStore } from '@/stores/app';
import { useErrorStore } from '@/stores/error';

type Translator = (key: string, named: Record<string, unknown>, plural: number) => string;

let translator: Translator | undefined;

/**
 * How the refusal toast translates without importing vue-i18n. `main.ts` calls it next to
 * `setErrorTranslator`. With none set, as in a test that sets none, the toast reads the bare key.
 */
export const setItemRefusalTranslator = (translate: Translator): void => {
  translator = translate;
};

const translate = (key: string, named: Record<string, unknown> = {}, plural = 1): string =>
  translator?.(key, named, plural) ?? key;

/** The toast's title for each change. */
const TITLE_KEYS: Record<ItemStatus, string> = {
  locked: 'item_refusal_title_locked',
  unlocked: 'item_refusal_title_unlocked',
  archive: 'item_refusal_title_archive',
  active: 'item_refusal_title_active',
  delete: 'item_refusal_title_delete'
};

/**
 * What the refused rows are. The archive's rows are cases and entries. `open-case` is the one case
 * open in the entry list, which is not a selection and draws no row, so its toast gives the reason.
 */
export type RefusedRows = 'cases' | 'entries' | 'items' | 'open-case';

/**
 * The toast's detail. `named` counts the refused rows among those sent, and is absent for the open
 * case, whose toast gives the reason itself. `reached` counts refused entries that are no row on
 * screen: the change reached them through a case.
 */
const DETAIL_KEYS: Record<RefusedRows, { named?: string; reached: string }> = {
  cases: { named: 'item_refusal_count_cases', reached: 'item_refusal_count_reached' },
  entries: { named: 'item_refusal_count_entries', reached: 'item_refusal_count_reached' },
  items: { named: 'item_refusal_count_items', reached: 'item_refusal_count_reached' },
  'open-case': { reached: 'item_refusal_count_reached_case' }
};

interface Row {
  identifier: string;
}

interface Change<T extends Row> {
  status: ItemStatus;
  rows: T[];
  of: RefusedRows;
}

/**
 * One store's last refused lock, unlock, archive, restore or delete: which rows the server refused
 * and why, for the list to draw beside each one.
 *
 * Kept until the next action starts or the selection is dropped. A refusal changed nothing, so the
 * list on screen is still the one the reasons are about.
 */
export const createItemRefusals = () => {
  const reasons = ref<Record<string, ItemRefusalReason>>({});
  /** Whether the last refusal named an item that is no row sent: an entry inside a sent case. */
  const reachedPastRows = ref(false);

  /** The reason the last refusal gave for this row, if it named it. */
  const reasonFor = (identifier?: string): ItemRefusalReason | undefined =>
    identifier ? reasons.value[identifier.toLowerCase()] : undefined;

  const clear = () => {
    reasons.value = {};
    reachedPastRows.value = false;
  };

  /**
   * The rows that stay selected after a refusal: the ones it named. When it also refused an entry
   * inside one of the cases, nothing says which case, so every row stays.
   */
  const refusedSelection = <T extends Row>(rows: T[]): T[] =>
    reachedPastRows.value ? rows : rows.filter(row => reasonFor(row.identifier));

  const detail = <T extends Row>(change: Change<T>, refused: RefusedItem[]): string => {
    const keys = DETAIL_KEYS[change.of];
    const named = change.rows
      .map(row => reasonFor(row.identifier))
      .filter((reason): reason is ItemRefusalReason => reason !== undefined);
    const reached = refused.length - named.length;
    const parts: string[] = [];
    if (named.length > 0) {
      const total = change.rows.length;
      parts.push(keys.named ?
        translate(keys.named, { refused: named.length, total }, total) :
        translate(ITEM_REFUSAL_REASON_KEYS[named[0]]));
    }
    if (reached > 0) {
      parts.push(translate(keys.reached, { refused: reached }, reached));
    }
    return parts.join(' ');
  };

  /**
   * Presents a failed change. A refusal records each refused row's reason, toasts how many were
   * refused, and returns true. Any other failure goes to `loader.setError`, as it always has.
   *
   * A refusal is reported without failing `loader`: the archive's loader is also its list's, and a
   * failed one draws an error state whose Retry re-reads the list and drops these reasons.
   */
  const fail = <T extends Row>(error: unknown, loader: LoadingStateModel, change: Change<T>): boolean => {
    const refused = refusedItemsOf(error);
    if (!refused) {
      loader.setError(error);
      return false;
    }
    useErrorStore().handle(error, { tier: ErrorTier.INLINE });
    reasons.value = Object.fromEntries(refused.map(item => [item.id, item.reason]));
    const sent = new Set(change.rows.map(row => row.identifier.toLowerCase()));
    reachedPastRows.value = refused.some(item => !sent.has(item.id));
    useAppStore().addToast(prepareToast('error', translate(TITLE_KEYS[change.status]), detail(change, refused), 6000));
    return true;
  };

  return { reasonFor, clear, refusedSelection, fail };
};
